import React from 'react';
import { Field, Section } from './ui';
import { ANNIVERSARY_REGIONS } from '../utils/holidays';

/*
 * The club's own details: who it is, what it has, when membership renews,
 * and who to contact.
 *
 * Four grey-tinted bordered boxes stacked inside the settings card, each
 * with a bold icon-and-caps heading, and every field label carrying its own
 * icon. That is a box inside a box inside a card, and the icons were doing
 * no work — nobody needs a telephone glyph to know that "Phone" is a phone
 * number. Sections and hairlines instead.
 */

const PROFILE_FIELDS = [
  { key: 'name', label: 'Club name' },
  { key: 'address', label: 'Address' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Main club email' },
  { key: 'website', label: 'Website' },
];

const COMMITTEE_ROLES = [
  { key: 'presidentEmail', label: 'President' },
  { key: 'vicePresidentEmail', label: 'Vice president' },
  { key: 'treasurerEmail', label: 'Treasurer' },
  { key: 'secretaryEmail', label: 'Secretary' },
  { key: 'managerEmail', label: 'Club manager' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function ClubGeneralSettings({ addOtherClubContact, club, clubContacts, clubEditForm, removeOtherClubContact, setClubEditForm, updateClubContactField, updateOtherClubContact }) {
  const set = (key, value) => setClubEditForm(f => ({ ...f, [key]: value }));

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <Section label="Club profile">
        <div className="gs-field-grid">
          {PROFILE_FIELDS.map(field => (
            <React.Fragment key={field.key}>
              {/* One line in the profile row, not a section of its own. All
                  it decides is which anniversary Monday the calendar
                  marks. */}
              {field.key === 'address' && (
                <Field label="Region" htmlFor="club-region">
                  <select id="club-region" className="w3-select"
                    value={clubEditForm.anniversaryRegion || ''}
                    onChange={e => set('anniversaryRegion', e.target.value)}>
                    <option value="">— None —</option>
                    {ANNIVERSARY_REGIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label={field.label} htmlFor={`club-${field.key}`}>
                <input
                  id={`club-${field.key}`}
                  className="w3-input"
                  value={clubEditForm[field.key] || ''}
                  onChange={e => set(field.key, e.target.value)} />
              </Field>
            </React.Fragment>
          ))}
        </div>
      </Section>

      <Section label="Facilities">
        <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)' }}>
          <Field label="Greens" htmlFor="club-greens">
            <select id="club-greens" className="w3-select"
              value={clubEditForm.numberOfGreens || 1}
              onChange={e => set('numberOfGreens', parseInt(e.target.value, 10) || 1)}>
              {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Rinks" htmlFor="club-rinks">
            <select id="club-rinks" className="w3-select"
              value={clubEditForm.numberOfRinks || 6}
              onChange={e => set('numberOfRinks', parseInt(e.target.value, 10) || 6)}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      {/* Not the renewal month below: plenty of clubs collect subs at a
          different time from when the green opens. This one decides what
          "2025–26" means in the year filter, the leaderboard and analytics. */}
      <Section label="Playing season">
        <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)' }}>
          <Field label="Season starts in" htmlFor="club-season-start"
            help="Competitions, results and the leaderboard are grouped by this season. Most clubs play July to June.">
            <select id="club-season-start" className="w3-select"
              value={clubEditForm.seasonStartMonth || ''}
              onChange={e => set('seasonStartMonth', e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">January (calendar year)</option>
              {MONTHS.slice(1).map((name, idx) => <option key={name} value={idx + 2}>{name}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section label="Membership cycle">
        <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)' }}>
          <Field label="Renewal month" htmlFor="club-renew-month"
            help="Rolling means each member renews twelve months after they joined.">
            <select id="club-renew-month" className="w3-select"
              value={clubEditForm.membershipRenewalMonth || ''}
              onChange={e => set('membershipRenewalMonth', e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">Rolling (12 months)</option>
              {MONTHS.map((name, idx) => <option key={name} value={idx + 1}>{name}</option>)}
            </select>
          </Field>
          <Field label="Renewal day" htmlFor="club-renew-day">
            <select id="club-renew-day" className="w3-select"
              value={clubEditForm.membershipRenewalDay || ''}
              onChange={e => set('membershipRenewalDay', e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">Auto</option>
              {Array.from({ length: 31 }).map((_, idx) => <option key={idx + 1} value={idx + 1}>{idx + 1}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section label="Committee contacts">
        <div className="gs-field-grid">
          {COMMITTEE_ROLES.map(contact => (
            <Field key={contact.key} label={contact.label} htmlFor={`club-${contact.key}`}>
              <input
                id={`club-${contact.key}`}
                className="w3-input"
                type="email"
                placeholder="name@club.org"
                value={clubContacts[contact.key] || ''}
                onChange={e => updateClubContactField(contact.key, e.target.value)} />
            </Field>
          ))}
        </div>
      </Section>

      <Section
        label="Other contacts"
        action={
          <button type="button" className="w3-button w3-round w3-theme-l4"
            style={{ minHeight: 'var(--gs-control-sm)' }}
            onClick={addOtherClubContact}>
            <i className="fa fa-plus w3-margin-right"></i>Add contact
          </button>
        }>
        {clubContacts.others.length === 0 ? (
          <p style={{ color: 'var(--gs-text-faint)', margin: 0 }}>No additional contacts yet.</p>
        ) : clubContacts.others.map((entry, idx) => (
          <div key={`other-${idx}`}
            className="gs-field-grid"
            style={{ '--gs-cols': 'minmax(180px, 1fr) minmax(220px, 1fr) auto', alignItems: 'end', marginBottom: 'var(--gs-space-3)' }}>
            <Field label="Role or title" htmlFor={`other-label-${idx}`}>
              <input id={`other-label-${idx}`} className="w3-input"
                value={entry.label || ''}
                placeholder="e.g. Junior Coordinator"
                onChange={e => updateOtherClubContact(idx, 'label', e.target.value)} />
            </Field>
            <Field label="Email" htmlFor={`other-email-${idx}`}>
              <input id={`other-email-${idx}`} className="w3-input" type="email"
                value={entry.email || ''}
                placeholder="name@club.org"
                onChange={e => updateOtherClubContact(idx, 'email', e.target.value)} />
            </Field>
            <button
              type="button"
              className="w3-button w3-round w3-hover-red"
              style={{ minHeight: 'var(--gs-control-md)', color: 'var(--gs-text-faint)' }}
              onClick={() => removeOtherClubContact(idx)}
              title={`Remove ${entry.label || 'contact'}`} aria-label={`Remove ${entry.label || 'contact'}`}>
              <i className="fa fa-trash"></i>
            </button>
          </div>
        ))}
      </Section>
    </div>
  );
}

export default ClubGeneralSettings;
